import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import useSectionReveal from '@/hooks/useSectionReveal';
import onView from '@/hooks/onView';
import { lightComfort } from '@/mocks/sections';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export default function LightComfortSection() {
  const ref = useRef<HTMLElement>(null);
  useSectionReveal(ref);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    const build = (move: number, peak: number) => {
      // Stage A — mask reveal + 26px settle
      const entry = gsap.timeline({ paused: true })
        .fromTo('.lc-frame', { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.05, ease: 'power3.out' })
        .fromTo('.lc-img', { y: 26 }, { y: 0, duration: 1.1, ease: 'power3.out' }, 0);
      const off = onView(ref.current?.querySelector('.lc-frame'), () => entry.restart(), () => entry.pause(0));

      // Stage B/D — light drifts across, peaks mid-way, settles on exit
      gsap.timeline({ scrollTrigger: { trigger: '.lc-frame', start: 'top 80%', end: 'bottom 20%', scrub: 0.9 } })
        .fromTo('.lc-light', { xPercent: -move, opacity: 0 }, { xPercent: 0, opacity: peak, duration: 0.5, ease: 'sine.inOut' })
        .to('.lc-light', { xPercent: move, opacity: 0.02, duration: 0.5, ease: 'sine.inOut' });

      // Stage C — benefits activate in sequence
      gsap.timeline({ scrollTrigger: { trigger: '.lc-points', start: 'top 82%', toggleActions: 'play none none reverse' } })
        .fromTo('.lc-line', { scaleX: 0 }, { scaleX: 1, duration: 0.65, ease: 'power3.out', stagger: 0.1 })
        .fromTo('.lc-title', { y: 10, opacity: 0.55 }, { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out', stagger: 0.1 }, 0.05)
        .fromTo('.lc-text', { opacity: 0.55 }, { opacity: 1, duration: 0.6, ease: 'power2.out', stagger: 0.1 }, 0.12);
      return off;
    };
    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => build(16, 1));
    mm.add('(min-width: 768px) and (max-width: 1023px) and (prefers-reduced-motion: no-preference)', () => build(10, 0.8));
    mm.add('(max-width: 767px) and (prefers-reduced-motion: no-preference)', () => build(6, 0.6));
    return () => mm.revert();
  }, { scope: ref });

  return (
    <section ref={ref} id="comfort" className="bg-primary-950 text-background-50 px-4 md:px-10 py-20 md:py-32 overflow-x-clip">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-6">
            <p data-reveal className="text-[11px] uppercase tracking-[0.24em] text-accent-300">{lightComfort.eyebrow}</p>
            <h2 data-reveal className="mt-4 font-heading font-light text-4xl md:text-6xl leading-[1.08]">{lightComfort.title}</h2>
          </div>
          <p data-reveal className="lg:col-span-5 lg:col-start-8 self-end text-sm md:text-base text-background-200 leading-relaxed">{lightComfort.text}</p>
        </div>
        <div className="lc-frame relative mt-12 md:mt-16 w-full h-[300px] md:h-[600px] overflow-hidden">
          <img loading="lazy" decoding="async" src={lightComfort.image} alt="Forest seen through a large window" title="Natural light and living comfort" className="lc-img w-full h-full object-cover object-top will-change-transform" />
          {/* Soft sunlight wash, clipped to the photo */}
          <div aria-hidden className="lc-light pointer-events-none absolute -inset-x-[20%] inset-y-0 opacity-0 mix-blend-soft-light will-change-transform lc-light-grad" />
        </div>
        <div className="lc-points mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-3">
          {lightComfort.points.map((p, i) => (
            <div key={p.title} className={`relative pt-6 pb-2 md:pr-8 ${i > 0 ? 'md:border-l md:pl-8' : ''} border-background-50/15`}>
              <span className="absolute left-0 right-0 top-0 h-px bg-background-50/15" />
              <span className="lc-line absolute left-0 right-0 top-0 h-px origin-left bg-accent-300/70" />
              <h3 className="lc-title font-heading text-xl">{p.title}</h3>
              <p className="lc-text mt-2 text-sm text-background-300 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}